'use client'

import React from 'react'
import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { ChevronRight, Search } from 'lucide-react'
import { cva } from 'class-variance-authority'
import { useTranslation } from 'react-i18next'
import { cn } from '@comps/lib/utils'
import { matchesSearch, normalizeSearch } from '@utils/search'

const treeVariants = cva(
    'group hover:before:opacity-100 before:absolute before:rounded-lg before:left-0 px-2 before:w-full before:opacity-0 before:bg-accent/70 before:h-[2rem] before:-z-10'
)

const selectedTreeVariants = cva(
    'before:opacity-100 before:bg-accent/70 text-accent-foreground'
)

interface TreeDataItem {
    id: string
    name: string
    icon?: any
    selectedIcon?: any
    openIcon?: any
    children?: TreeDataItem[]
    actions?: React.ReactNode
    onClick?: () => void
}

type TreeProps = React.HTMLAttributes<HTMLDivElement> & {
    data: TreeDataItem[] | TreeDataItem
    initialSelectedItemId?: string
    onSelectChange?: (item: TreeDataItem | undefined) => void
    expandAll?: boolean
    defaultNodeIcon?: any
    defaultLeafIcon?: any
    minLevel?: number
    style?: React.CSSProperties
    // searchable adds a search input on top of the tree: only the branches
    // holding a matching leaf are kept, all expanded.
    searchable?: boolean
}

// filterTree keeps the leaves whose name matches the query and the branches
// leading to them; branches without any matching leaf are pruned. An empty
// query returns the items unchanged.
function filterTree(data: TreeDataItem[] | TreeDataItem, query: string): TreeDataItem[] {
    const items = data instanceof Array ? data : [data]
    if (normalizeSearch(query) === '') {
        return items
    }
    return items.flatMap((item) => {
        if (!item.children) {
            return matchesSearch(item.name, query) ? [item] : []
        }
        const children = filterTree(item.children, query)
        return children.length > 0 ? [{ ...item, children }] : []
    })
}

// collectLeaves lists the leaves in display order.
function collectLeaves(items: TreeDataItem[]): TreeDataItem[] {
    return items.flatMap((item) => (item.children ? collectLeaves(item.children) : [item]))
}

const TreeView = React.forwardRef<HTMLDivElement, TreeProps>(
    (
        {
            data,
            initialSelectedItemId,
            onSelectChange,
            expandAll,
            defaultLeafIcon,
            defaultNodeIcon,
            minLevel,
            className,
            style,
            searchable,
            ...props
        },
        ref
    ) => {
        const { t } = useTranslation()
        const [search, setSearch] = React.useState('')
        // Index of the search result highlighted for the keyboard.
        const [activeIndex, setActiveIndex] = React.useState(0)
        const containerRef = React.useRef<HTMLDivElement>(null)
        const [selectedItemId, setSelectedItemId] = React.useState<
            string | undefined
        >(initialSelectedItemId)

        const handleSelectChange = React.useCallback(
            (item: TreeDataItem | undefined) => {
                setSelectedItemId(item?.id)
                if (onSelectChange) {
                    onSelectChange(item)
                }
            },
            [onSelectChange]
        )

        const expandedItemIds = React.useMemo(() => {
            if (!initialSelectedItemId) {
                return [] as string[]
            }

            const ids: string[] = []

            function walkTreeItems(
                items: TreeDataItem[] | TreeDataItem,
                targetId: string
            ) {
                if (items instanceof Array) {
                    for (let i = 0; i < items.length; i++) {
                        ids.push(items[i]!.id)
                        if (walkTreeItems(items[i]!, targetId) && !expandAll) {
                            return true
                        }
                        if (!expandAll) ids.pop()
                    }
                } else if (!expandAll && items.id === targetId) {
                    return true
                } else if (items.children) {
                    return walkTreeItems(items.children, targetId)
                }
            }

            walkTreeItems(data, initialSelectedItemId)
            return ids
        }, [data, expandAll, initialSelectedItemId])

        const isSearching = !!searchable && normalizeSearch(search) !== ''
        const filteredData = React.useMemo(
            () => (isSearching ? filterTree(data, search) : null),
            [data, isSearching, search]
        )
        const visibleData = filteredData ?? data
        const noResults = filteredData?.length === 0

        // While searching, every branch is expanded: the arrows move through
        // the matching leaves, as in the searchable lists (cmdk).
        const resultLeaves = React.useMemo(() => {
            if (!filteredData) {
                return []
            }
            const roots = minLevel ? filteredData.filter((item) => isDeeperThanMinLevel(item, minLevel)) : filteredData
            return collectLeaves(roots)
        }, [filteredData, minLevel])
        const activeLeaf = resultLeaves[Math.min(activeIndex, resultLeaves.length - 1)]

        React.useEffect(() => {
            containerRef.current?.querySelector('[data-active="true"]')?.scrollIntoView?.({ block: 'nearest' })
        }, [activeLeaf])

        function onSearchChange(value: string) {
            setSearch(value)
            setActiveIndex(0)
        }

        function onSearchKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
            if (event.key === 'Enter') {
                // Enter never submits an enclosing form.
                event.preventDefault()
                if (activeLeaf) {
                    handleSelectChange(activeLeaf)
                    activeLeaf.onClick?.()
                }
            } else if (event.key === 'ArrowDown' && resultLeaves.length > 0) {
                event.preventDefault()
                setActiveIndex(Math.min(activeIndex + 1, resultLeaves.length - 1))
            } else if (event.key === 'ArrowUp' && resultLeaves.length > 0) {
                event.preventDefault()
                setActiveIndex(Math.max(activeIndex - 1, 0))
            }
        }

        return (
            <div ref={containerRef} className={cn('overflow-hidden relative p-2', className)} style={style}>
                {searchable && (
                    <TreeSearchInput
                        value={search}
                        onChange={onSearchChange}
                        onKeyDown={onSearchKeyDown}
                        placeholder={t('field.search.placeholder')}
                    />
                )}
                {noResults ? (
                    <div className="w-full py-4 text-center text-sm text-muted-foreground">
                        {t('field.search.noResults')}
                    </div>
                ) : (
                    <TreeItem
                        // Remounted on each search so that the nodes start
                        // expanded while searching and collapsed once cleared.
                        key={isSearching ? 'search:' + search : 'browse'}
                        data={visibleData}
                        ref={ref}
                        selectedItemId={selectedItemId}
                        activeItemId={activeLeaf?.id}
                        handleSelectChange={handleSelectChange}
                        expandedItemIds={expandedItemIds}
                        forceExpanded={isSearching}
                        defaultLeafIcon={defaultLeafIcon}
                        defaultNodeIcon={defaultNodeIcon}
                        minLevel={minLevel}
                        level={0}
                        {...props}
                    />
                )}
            </div>
        )
    }
)
TreeView.displayName = 'TreeView'

// TreeSearchInput mirrors the cmdk CommandInput look, sticky on top of the
// scrolling tree.
const TreeSearchInput = ({
    value,
    onChange,
    onKeyDown,
    placeholder
}: {
    value: string
    onChange: (value: string) => void
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
    placeholder: string
}) => {
    return (
        <div className="sticky top-0 z-10 flex w-full shrink-0 items-center border-b bg-white px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
                type="text"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                className="flex h-10 w-full bg-transparent py-3 text-sm text-gray-800 outline-none placeholder:text-muted-foreground"
            />
        </div>
    )
}

type TreeItemProps = TreeProps & {
    selectedItemId?: string
    activeItemId?: string
    handleSelectChange: (item: TreeDataItem | undefined) => void
    expandedItemIds: string[]
    forceExpanded?: boolean
    defaultNodeIcon?: any
    defaultLeafIcon?: any
    level: number
    minLevel?: number
}

function isDeeperThanMinLevel(item: TreeDataItem, minLevel?: number): boolean {
    if (!minLevel || minLevel <= 0) return true;
    return (
        item.children &&
        item.children.length > 0 &&
        item.children.some((child) => isDeeperThanMinLevel(child, minLevel - 1))
    ) || false
}

const TreeItem = React.forwardRef<HTMLDivElement, TreeItemProps>(
    (
        {
            className,
            data,
            selectedItemId,
            activeItemId,
            handleSelectChange,
            expandedItemIds,
            forceExpanded,
            defaultNodeIcon,
            defaultLeafIcon,
            level,
            minLevel,
            ...props
        },
        ref
    ) => {
        if (!(data instanceof Array)) {
            data = [data]
        }

        if (minLevel && level === 0) {
            data = data.filter((item) => isDeeperThanMinLevel(item, minLevel))
        }

        return (
            <div ref={ref} role="tree" className={className} {...props}>
                <ul>
                    {data.map((item) => (
                        <li key={item.id}>
                            {item.children ? (
                                <TreeNode
                                    item={item}
                                    selectedItemId={selectedItemId}
                                    activeItemId={activeItemId}
                                    expandedItemIds={expandedItemIds}
                                    forceExpanded={forceExpanded}
                                    handleSelectChange={handleSelectChange}
                                    defaultNodeIcon={defaultNodeIcon}
                                    defaultLeafIcon={defaultLeafIcon}
                                    level={level + 1}
                                    minLevel={minLevel}
                                />
                            ) : (
                                <TreeLeaf
                                    item={item}
                                    selectedItemId={selectedItemId}
                                    isActive={activeItemId === item.id}
                                    handleSelectChange={handleSelectChange}
                                    defaultLeafIcon={defaultLeafIcon}
                                    level={level + 1}
                                    minLevel={minLevel}
                                />
                            )}
                        </li>
                    ))}
                </ul>
            </div>
        )
    }
)
TreeItem.displayName = 'TreeItem'

const TreeNode = ({
    item,
    handleSelectChange,
    expandedItemIds,
    forceExpanded,
    selectedItemId,
    activeItemId,
    defaultNodeIcon,
    defaultLeafIcon,
    level,
    minLevel
}: {
    item: TreeDataItem
    handleSelectChange: (item: TreeDataItem | undefined) => void
    expandedItemIds: string[]
    forceExpanded?: boolean
    selectedItemId?: string
    activeItemId?: string
    defaultNodeIcon?: any
    defaultLeafIcon?: any
    level: number
    minLevel?: number
}) => {
    const [value, setValue] = React.useState(
        forceExpanded || expandedItemIds.includes(item.id) ? [item.id] : []
    )
    return (
        <AccordionPrimitive.Root
            type="multiple"
            value={value}
            onValueChange={(s) => setValue(s)}
        >
            <AccordionPrimitive.Item value={item.id}>
                <AccordionTrigger
                    className={cn(
                        treeVariants(),
                        selectedItemId === item.id && selectedTreeVariants()
                    )}
                    onClick={() => {
                        handleSelectChange(item)
                        item.onClick?.()
                    }}
                >
                    <TreeIcon
                        item={item}
                        isSelected={selectedItemId === item.id}
                        isOpen={value.includes(item.id)}
                        default={defaultNodeIcon}
                    />
                    <span className="text-sm truncate">{item.name}</span>
                    <TreeActions isSelected={selectedItemId === item.id}>
                        {item.actions}
                    </TreeActions>
                </AccordionTrigger>
                <AccordionContent className="ml-4 pl-1 border-l">
                    <TreeItem
                        data={item.children ? item.children : item}
                        selectedItemId={selectedItemId}
                        activeItemId={activeItemId}
                        handleSelectChange={handleSelectChange}
                        expandedItemIds={expandedItemIds}
                        forceExpanded={forceExpanded}
                        defaultLeafIcon={defaultLeafIcon}
                        defaultNodeIcon={defaultNodeIcon}
                        level={level + 1}
                        minLevel={minLevel}
                    />
                </AccordionContent>
            </AccordionPrimitive.Item>
        </AccordionPrimitive.Root>
    )
}

const TreeLeaf = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> & {
        item: TreeDataItem
        selectedItemId?: string
        // isActive highlights the search result that Enter would pick.
        isActive?: boolean
        handleSelectChange: (item: TreeDataItem | undefined) => void
        defaultLeafIcon?: any
        level: number
        minLevel?: number
    }
>(
    (
        {
            className,
            item,
            selectedItemId,
            isActive,
            handleSelectChange,
            defaultLeafIcon,
            level,
            minLevel,
            ...props
        },
        ref
    ) => {
        return (
            <div
                ref={ref}
                className={cn(
                    'ml-5 flex text-left items-center py-2 cursor-pointer before:right-1 treeLeaf',
                    treeVariants(),
                    className,
                    selectedItemId === item.id && selectedTreeVariants(),
                    isActive && 'rounded-md bg-accent text-accent-foreground'
                )}
                data-active={isActive || undefined}
                onClick={() => {
                    handleSelectChange(item)
                    item.onClick?.()
                }}
                {...props}
            >
                <TreeIcon
                    item={item}
                    isSelected={selectedItemId === item.id}
                    default={defaultLeafIcon}
                />
                <span className="flex-grow text-sm truncate">{item.name}</span>
                <TreeActions isSelected={selectedItemId === item.id}>
                    {item.actions}
                </TreeActions>
            </div>
        )
    }
)
TreeLeaf.displayName = 'TreeLeaf'

const AccordionTrigger = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
    <AccordionPrimitive.Header>
        <AccordionPrimitive.Trigger
            ref={ref}
            className={cn(
                'flex flex-1 w-full items-center py-2 transition-all first:[&[data-state=open]>svg]:rotate-90',
                className
            )}
            {...props}
        >
            <ChevronRight className="h-4 w-4 shrink-0 transition-transform duration-200 text-accent-foreground/50 mr-1" />
            {children}
        </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
))
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName

const AccordionContent = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
    <AccordionPrimitive.Content
        ref={ref}
        className={cn(
            'overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
            className
        )}
        {...props}
    >
        <div className="pb-1 pt-0">{children}</div>
    </AccordionPrimitive.Content>
))
AccordionContent.displayName = AccordionPrimitive.Content.displayName

const TreeIcon = ({
    item,
    isOpen,
    isSelected,
    default: defaultIcon
}: {
    item: TreeDataItem
    isOpen?: boolean
    isSelected?: boolean
    default?: any
}) => {
    let Icon = defaultIcon
    if (isSelected && item.selectedIcon) {
        Icon = item.selectedIcon
    } else if (isOpen && item.openIcon) {
        Icon = item.openIcon
    } else if (item.icon) {
        Icon = item.icon
    }
    return Icon ? (
        <Icon className="h-4 w-4 shrink-0 mr-2" />
    ) : (
        <></>
    )
}

const TreeActions = ({
    children,
    isSelected
}: {
    children: React.ReactNode
    isSelected: boolean
}) => {
    return (
        <div
            className={cn(
                isSelected ? 'block' : 'hidden',
                'absolute right-3 group-hover:block'
            )}
        >
            {children}
        </div>
    )
}

export { TreeView, filterTree, type TreeDataItem }